/**
 * Plan Executor Policy
 *
 * Sequentially dispatches commands from a command plan to target stores.
 * Reacts when executingPlanMessageId is set or currentExecutingStepIndex changes.
 *
 * Allowed step types:
 * - `__navigate`        — route change via the registered navigate function
 * - `__human`           — pause execution and wait for the user
 * - any CDSE store name — UI-fill dispatched via `dispatchToStore` with `source: 'ai'`
 *
 * Backend mutations are NOT executed from client plans. They run server-side
 * through the loop's gated capability tools. A plan containing a
 * `__backend_command` step is REJECTED at validation.
 *
 * Behavior:
 * - Pre-flight validation: rejects empty commandType and any `__backend_command`
 *   step (no longer supported). A store step is only rejected up-front for being
 *   unmounted when NO `__navigate` precedes it — a post-navigate store mounts
 *   asynchronously after the route change, so its mount check is deferred to
 *   execution time. Rejecting it up-front would abort the navigate that makes
 *   the store appear (the bug that left open_create_form / draft_* unable to
 *   open any form).
 * - Store steps use a bounded mount-wait retry (~2s) to absorb the
 *   navigate -> page-mount -> store-register latency.
 * - StoreNotMountedError / CommandRejectedError surface as user-facing
 *   messages on the failed step.
 * - On any step failure: remaining steps marked as skipped, plan marked as failed.
 */

import { Policy, PolicyContext } from '@commandDrivenStateEngine/core/policies';
import { AIAssistantState } from '../state';
import { AIAssistantCommand } from '../commands';
import { dispatchToStore, getNavigate, getAllStores } from '@commandDrivenStateEngine/core/storeRegistry';
import { StoreNotMountedError } from '@commandDrivenStateEngine/core/errors';
import { CommandRejectedError } from '@commandDrivenStateEngine/core/errors';
import { AICommandStep } from '@infrastructure/aiAssistant/types';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import { resolvePrefillCommand, PrefillStepPayload } from '../prefillRegistry';

const STEP_DELAY_MS = 300;
const NAVIGATE_DELAY_MS = 800;

/**
 * Mount-wait retry for store steps. A store step typically follows a
 * `__navigate`; the destination page (and its CDSE store) mounts
 * asynchronously after the route change, so a `StoreNotMountedError` on the
 * first attempt is expected. We retry for a bounded window (~2s) before
 * failing the step. Tuned to comfortably cover a route transition + page mount.
 */
const MOUNT_RETRY_MS = 200;
const MAX_MOUNT_RETRIES = 10;

/** Removed step type. A plan containing this is rejected at validation. */
const REMOVED_STORE_NAMES = ['__backend_command'];

/**
 * Validate a plan before execution.
 * Returns an error message string if validation fails, or null if valid.
 */
function validatePlan( steps: AICommandStep[]): string | null {
  const registeredStoreNames = new Set( getAllStores().map( s => s.name ));
  let precededByNavigate = false;

  for ( const step of steps ) {
    // Every step must have a non-empty commandType
    if ( !step.commandType || typeof step.commandType !== 'string' || step.commandType.trim() === '' ) {
      return `Step "${step.id}" has an invalid or empty command type.`;
    }

    // Reject any reference to a removed special store (backend mutations now
    // run server-side through gated capability tools, not client plan steps).
    if ( REMOVED_STORE_NAMES.includes( step.storeName )) {
      return `Step "${step.id}" uses removed step type "${step.storeName}". Backend mutations are no longer executed from client plans.`;
    }

    if ( step.storeName === '__navigate' ) {
      precededByNavigate = true;
      continue;
    }
    if ( step.storeName === '__human' ) continue;

    // A store step is only validated for mount up-front when no `__navigate`
    // precedes it (a same-page plan). When a navigate comes first, the
    // destination page — and therefore its CDSE store — mounts AFTER the route
    // change, so the store is legitimately absent from the registry at
    // validation time. Rejecting here would also abort the navigate step that
    // makes the store appear, which is exactly the failure mode that made
    // navigate-then-act plans (open_create_form, draft_*) non-functional. The
    // mount check for post-navigate steps is deferred to execution time via the
    // bounded mount-wait retry below.
    if ( !registeredStoreNames.has( step.storeName ) && !precededByNavigate ) {
      return `Step "${step.id}" references store "${step.storeName}" which is not mounted. Navigate to the correct page first.`;
    }
  }

  return null;
}

/**
 * Mark remaining steps as skipped and fail the plan.
 */
function failPlanWithRemainingSkipped(
  ctx: PolicyContext<AIAssistantState, AIAssistantCommand>,
  steps: AICommandStep[],
  fromIndex: number,
  messageId: string
): void {
  // Mark all remaining steps (after the failed one) as skipped
  for ( let i = fromIndex; i < steps.length; i++ ) {
    ctx.dispatch(
      { type: 'aiAssistant/plan/stepCompleted', payload: {
        stepId: steps[i].id,
        result: { stepId: steps[i].id, status: 'skipped' }
      }},
      { source: 'application' }
    );
  }

  // Mark the plan as aborted (which sets planStatus to 'aborted' / failed state)
  ctx.dispatch(
    { type: 'aiAssistant/plan/aborted', payload: { messageId }},
    { source: 'application' }
  );
}

export const createPlanExecutorPolicy = (
  _backend: ClientLibraryType
): Policy<AIAssistantState, AIAssistantCommand> => ({
  name: 'plan-executor',

  shouldReact: ( prev, next ) => {
    // React when execution starts
    if ( !prev.executingPlanMessageId && next.executingPlanMessageId ) return true;
    // React when step index advances
    if (
      next.executingPlanMessageId &&
      prev.currentExecutingStepIndex !== next.currentExecutingStepIndex
    ) return true;
    return false;
  },

  onStateChange: ({ next, ctx }) => {
    if ( !next.executingPlanMessageId ) return;

    const message = next.messages.find( m => m.id === next.executingPlanMessageId );
    if ( !message?.commandPlan ) return;

    const { steps } = message.commandPlan;
    const stepIndex = next.currentExecutingStepIndex;

    // Pre-flight validation on first step (execution just started)
    if ( stepIndex === 0 ) {
      const validationError = validatePlan( steps );
      if ( validationError ) {
        ctx.dispatch(
          { type: 'aiAssistant/error/setPlanValidationError', payload: { error: validationError }},
          { source: 'application' }
        );
        // Mark all steps as skipped and fail the plan
        failPlanWithRemainingSkipped( ctx, steps, 0, next.executingPlanMessageId );
        return;
      }
    }

    // All steps done
    if ( stepIndex >= steps.length ) {
      ctx.dispatch(
        { type: 'aiAssistant/plan/completed', payload: { messageId: next.executingPlanMessageId }},
        { source: 'application' }
      );
      return;
    }

    const step = steps[stepIndex];

    // Mark step as executing (update result only, no index increment)
    ctx.dispatch(
      { type: 'aiAssistant/plan/stepStarted', payload: {
        stepId: step.id,
        result: { stepId: step.id, status: 'executing' }
      }},
      { source: 'application' }
    );

    // Human steps: pause execution and wait for humanStepCompleted from UI
    if ( step.storeName === '__human' ) return;

    const completeStep = () => {
      // Re-read state after the side-effect to check if execution was aborted
      const currentState = ctx.getState();
      if ( !currentState.executingPlanMessageId ) return;

      ctx.dispatch(
        { type: 'aiAssistant/plan/stepCompleted', payload: {
          stepId: step.id,
          result: { stepId: step.id, status: 'completed' }
        }},
        { source: 'application' }
      );
    };

    const failStep = ( error: unknown ) => {
      // Determine user-facing error message based on error type
      let errorMessage: string;
      if ( error instanceof StoreNotMountedError ) {
        errorMessage = error.message;
      } else if ( error instanceof CommandRejectedError ) {
        errorMessage = 'This action requires manual execution';
      } else {
        errorMessage = error instanceof Error ? error.message : String( error );
      }

      ctx.dispatch(
        { type: 'aiAssistant/plan/stepCompleted', payload: {
          stepId: step.id,
          result: { stepId: step.id, status: 'failed', error: errorMessage }
        }},
        { source: 'application' }
      );

      // Mark remaining steps as skipped and fail the plan
      failPlanWithRemainingSkipped( ctx, steps, stepIndex + 1, next.executingPlanMessageId! );
    };

    // Navigation step: run once after the navigate delay.
    if ( step.storeName === '__navigate' ) {
      setTimeout(() => {
        try {
          const navigate = getNavigate();
          if ( !navigate ) {
            throw new Error( 'Navigate function not registered' );
          }

          /**
           * Taking the user somewhere means the destination has to be VISIBLE.
           * In fullscreen the panel covers the whole app below the navbar, so a
           * navigation lands behind it and nothing appears to happen — the
           * assistant says "te llevo a X" and the user still sees the chat.
           * Drop to the floating window first, which is the mode where the page
           * and the conversation are on screen together.
           *
           * `source: 'application'` — this is a consequence of executing a plan
           * the user already approved, not a fresh user action.
           */
          if ( ctx.getState().panelMode === 'fullscreen' ) {
            ctx.dispatch(
              { type: 'aiAssistant/panel/setMode', payload: { mode: 'floating' }},
              { source: 'application' }
            );
          }

          navigate( step.payload as string );
          completeStep();
        } catch ( error ) {
          failStep( error );
        }
      }, NAVIGATE_DELAY_MS );
      return;
    }

    // CDSE store command — UI-fill, dispatched with source 'ai'. Bounded
    // mount-wait retry: when this step follows a `__navigate`, the destination
    // store mounts asynchronously, so a StoreNotMountedError on early attempts
    // is expected and retried for ~2s before the step is failed.
    //
    // A `prefill` step is a SEMANTIC step: its payload is `{ entity, field,
    // value }`, not a store command. We resolve it to the store's real
    // domain-specific command via the prefill registry before dispatch. An
    // unsupported (entity, field) throws (not a StoreNotMountedError), so it
    // falls straight to `failStep` and surfaces a real error instead of a
    // silent no-op.
    const runStoreStep = ( attemptsLeft: number ) => {
      try {
        const command = step.commandType === 'prefill'
          ? resolvePrefillCommand( step.payload as PrefillStepPayload )
          : { type: step.commandType, payload: step.payload };
        dispatchToStore(
          step.storeName,
          command,
          { source: 'ai' }
        );
        completeStep();
      } catch ( error ) {
        if ( error instanceof StoreNotMountedError && attemptsLeft > 0 ) {
          setTimeout(() => runStoreStep( attemptsLeft - 1 ), MOUNT_RETRY_MS );
          return;
        }
        failStep( error );
      }
    };

    setTimeout(() => runStoreStep( MAX_MOUNT_RETRIES ), STEP_DELAY_MS );
  }
});
